<!--
  NylonFormField — text-style input with floating label.

  Vetted Nylon-tier text-field primitive. Wraps an `<input>` plus
  its floating label and optional inline error / help text in a
  `<label>` element. The floating-label visual is CSS-driven via
  the `:placeholder-shown` trick — see _NylonFormField.scss.

  Use cases: email, name, address, phone, postal-code, password,
  short text. For long text use `<NylonFormTextarea>`. For dropdowns
  use `<NylonFormSelect>`. For checkboxes / radios use
  `<NylonCheckbox>`.

  Required: the input must have a non-empty `placeholder` for the
  floating-label mechanics to engage (CSS reads `:placeholder-shown`
  to decide whether to scale the label down or float it up). Pass
  the placeholder via `placeholderText` or via fall-through attr.

  Props:
    modelValue (String|Number) — v-model binding.
    type       (String)        — `<input type>`. Defaults to "text".
                                  Use "text"/"email"/"tel"/"url"/
                                  "search"/"password"/"number".
                                  Don't use "checkbox" or "radio"
                                  (they need different markup).
    label      (String)        — floating label copy.
    isError    (Boolean)       — error state (red border + "!"
                                  bubble + visible message slot).
    isDisabled (Boolean)       — disabled state.
    isSmall    (Boolean)       — compact 24×24 box (cart-row qty
                                  widget). Suppresses the floating
                                  label.

  Slots:
    default — arbitrary inline content rendered inside the <label>
              after the input + label span. Used for inline siblings
              like a status spinner or page-scoped feedback paragraphs
              (e.g. the checkout discount field's `<VueSpinner>` and
              `<p class="feedback">` block).
    message — inline error / help copy below the field. Typically
              rendered conditionally on validation state.
    note    — static help / hint copy below the field.

  Fall-through: `placeholder`, `name`, `autocomplete`, `required`,
  `pattern`, `maxlength`, `@change`, etc. forward to the underlying
  `<input>` — EXCEPT `class`/`style`, which land on the root `<label>`
  (the element a consumer means when styling the component; a class
  landing on the inner input silently broke the campaign date columns'
  50/50 layout — caught 2026-06-10).
-->
<template>
  <label
    class="nylon-FormField"
    :class="[
      $attrs.class,
      {
        'nylon-FormField--isError': isError,
        'nylon-FormField--isDisabled': isDisabled,
        'nylon-FormField--small': isSmall,
      },
    ]"
    :style="$attrs.style"
  >
    <input class="nylon-FormField-input" :type="type" :value="modelValue" :disabled="isDisabled" v-bind="inputAttrs" @input="onInput" />
    <span v-if="label" class="nylon-FormField-label">{{ label }}</span>
    <slot />
    <div v-if="$slots.message" class="nylon-FormField-message"><slot name="message" /></div>
    <div v-if="$slots.note" class="nylon-FormField-note"><slot name="note" /></div>
  </label>
</template>

<script>
export default {
  name: "NylonFormField",

  inheritAttrs: false,

  emits: ["update:modelValue"],

  props: {
    modelValue: {
      type: [String, Number],
      default: "",
    },
    type: {
      type: String,
      default: "text",
    },
    label: {
      type: String,
      default: "",
    },
    isError: {
      type: Boolean,
      default: false,
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    isSmall: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    // $attrs minus class/style — those belong to the root label, the
    // rest to the input.
    inputAttrs() {
      const { class: _class, style: _style, ...rest } = this.$attrs;
      return rest;
    },
  },

  methods: {
    onInput(event) {
      this.$emit("update:modelValue", event.target.value);
    },
  },
};
</script>
